import React, { useState } from 'react';
import classNames from 'classnames';
import { getLoadDataError, type LoadDataApiError } from '@mc/axios-api';

import { AppError } from '../error/AppError';
import { Spinner, SpinnerSize } from '../spinner/Spinner';

import type { UseQueryResult } from 'react-query';

interface Props {
  queries: UseQueryResult<any, any>[];
  children: (props: {
    isFetching: boolean;
    hasData: boolean;
  }) => React.ReactNode;
  fullscreen?: boolean;
  inline?: boolean;
  showContentWhenLoading?: boolean;
}

export const AppLayoutWaitForQueryData: React.FC<Props> = ({
  queries,
  children,
  fullscreen = false,
  inline = false,
  showContentWhenLoading = false,
}) => {
  const [open, setOpen] = useState<boolean>(false);

  let isError = false;
  let isLoading = false;
  let isSuccess = false;
  let isFetching = false;

  let errors: LoadDataApiError[] = [];
  queries.forEach((query) => {
    isFetching = isFetching || query.isFetching;
    isSuccess = isSuccess || query.isSuccess;

    if (query.isError) {
      isError = true;
      const error = getLoadDataError(query.error);
      errors.push(error);
    }

    if (query.isLoading) {
      isLoading = true;
    }
  });

  const hasData = !isLoading || (isLoading && showContentWhenLoading);

  const spinnerCls = classNames('wait-for-query-wait', {
    ['wait-for-query-wait-inline']: inline,
    ['d-flex align-items-center h-100']: fullscreen,
  });

  if (isError) {
    return (
      <AppError message={'Data se nepodařilo načíst'}>
        <div className={'d-flex align-items-center flex-column'}>
          <a
            href={'#'}
            onClick={() => {
              setOpen(!open);
            }}
          >
            {open ? 'Skrýt podrobnosti' : 'Zobrazit podrobnosti'}
          </a>
          {open && (
            <div className={'mt-2'}>
              {errors.map((error, index) => {
                return (
                  <div style={{ textAlign: 'left' }}>
                    {index + 1}.{' '}
                    {error.message || 'Při načítání dat nastala obecná chyba'}
                    {error.description && (
                      <div>
                        <small>{error.description}</small>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </AppError>
    );
  }

  if (isLoading && !showContentWhenLoading) {
    return (
      <Spinner
        size={inline ? SpinnerSize.SMALL : SpinnerSize.STANDARD}
        className={spinnerCls}
      />
    );
  }

  return (
    <React.Fragment>
      {(children as any)({ isFetching, hasData })}
    </React.Fragment>
  );
};
